import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { Box } from '@mui/material';
import { Link } from 'react-router-dom';

import YoutubeEmbed from './YoutubeEmbed';

// Page texts live in public/content/<name>.md so People can edit them without touching code.
const contentUrl = (name: string) => `/content/${name}.md`;

// Relative image paths in the .md files are relative to public/content/.
const imageUrl = (src = '') =>
  /^(https?:)?\/\//.test(src) || src.startsWith('/') ? src : `/content/${src}`;

const youtubeId = (text: string): string | undefined =>
  /^https:\/\/(?:www\.)?(?:youtube\.com\/watch\?v=|youtu\.be\/)([\w-]{11})\S*$/.exec(text.trim())?.[1];

type HastNode = {
  type: string;
  tagName?: string;
  value?: string;
  properties?: { src?: string; alt?: string };
  children?: HastNode[];
};

const findImage = (node?: HastNode): HastNode | undefined =>
  node?.tagName === 'img' ? node : node?.children?.map(findImage).find(Boolean);

// Inside benefit rows and quotes the image is drawn by the row itself, not inline.
const ImageLifted = createContext(false);

const InlineImage = ({ src, alt }: { src?: string; alt?: string }) =>
  useContext(ImageLifted) ? null : <img alt={alt ?? ''} src={imageUrl(src)} style={{ maxWidth: '100%' }} />;

const MediaRow = ({ node, className, children, as: Tag = 'div' }: { node?: HastNode; className: string; children: ReactNode; as?: 'div' | 'figure' }) => {
  const image = findImage(node);

  if (!image) return <>{children}</>;

  return (
    <Tag className={className}>
      <img alt={image.properties?.alt ?? ''} src={imageUrl(image.properties?.src)} />
      <div>
        <ImageLifted.Provider value>{children}</ImageLifted.Provider>
      </div>
    </Tag>
  );
};

// Colours and type of phatconsulting.de: dark blue text, DIN body, dotted LED headings.
const lightStyles = {
  color: '#344853',
  fontFamily: 'DIN2014-Regular, Helvetica, Arial, sans-serif',
  fontSize: '18px',
  lineHeight: 1.25,
  padding: '25px 0',
  '& h1, & h2': { fontFamily: 'LedRegular', fontSize: '2.2rem', fontWeight: 'normal', textAlign: 'left', margin: '10px 0 1rem' },
  '& strong': { fontFamily: 'DIN2014-Bold, Helvetica, Arial, sans-serif', fontWeight: 'normal' },
  '& a': { color: '#344853' },
  '& h2': { marginTop: '50px' },
  '& p': { margin: '0 0 0.85rem' },
  '& ul': { margin: '0 0 1rem', paddingLeft: '1.25rem' },
  '& li': { marginBottom: '0.45rem' },
  '& ul:has(.benefit)': { listStyle: 'none', paddingLeft: 0 },
  '& .benefit': { display: 'flex', alignItems: 'center', marginTop: '20px' },
  '& .benefit img': { width: '155px', marginRight: '4px', flex: 'none' },
  '& .quote': { display: 'flex', alignItems: 'center', margin: '0 0 40px' },
  '& .quote:nth-of-type(even)': { flexDirection: 'row-reverse' },
  '& .quote img': { width: '200px', margin: '0 24px', flex: 'none' },
  '& .quote > div': { flex: 1 },
  '& .quote p': { margin: 0 },
  '& .quote strong': { display: 'block' },
  '@media (max-width: 1000px)': {
    '& .benefit img': { width: '80px', marginLeft: '-12px' },
    '& .quote, & .quote:nth-of-type(even)': { flexDirection: 'column', textAlign: 'center' },
    '& .quote img': { width: '32%', margin: '0 8px 8px' },
  },
};

// Section intro of phatconsulting.de ("Wir sind PHAT."): bold DIN heading, LED lead, dotted rule.
const darkStyles = {
  color: '#fff',
  '& h1': { fontFamily: 'DIN2014-Bold, Helvetica, Arial, sans-serif', fontSize: '42px', fontWeight: 'normal', lineHeight: 1.1, margin: '0 0 24px' },
  '& p': { fontFamily: 'LedRegular, Arial, sans-serif', fontSize: '22px', lineHeight: 1.3, margin: 0, paddingBottom: '20px', borderBottom: '2px dotted rgba(255,255,255,0.8)' },
  '& a': { color: '#97d700', textDecoration: 'none' },
};

type MarkdownPageProps = {
  name: string;
  variant?: 'light' | 'dark';
};

const MarkdownPage = ({ name, variant = 'light' }: MarkdownPageProps): JSX.Element | null => {
  const [markdown, setMarkdown] = useState<string>();

  useEffect(() => {
    fetch(contentUrl(name))
      // The SPA fallback answers unknown paths with index.html; never render that as text.
      .then((response) =>
        response.ok && !(response.headers.get('content-type') ?? '').includes('text/html')
          ? response.text()
          : '',
      )
      .then(setMarkdown)
      .catch(() => setMarkdown(''));
  }, [name]);

  if (markdown === undefined) return null;

  return (
    <Box sx={variant === 'dark' ? darkStyles : lightStyles}>
      <ReactMarkdown
        components={{
          img: ({ src, alt }) => <InlineImage alt={alt} src={src} />,
          a: ({ href = '', children }) =>
            href.startsWith('/') ? (
              <Link to={href}>{children}</Link>
            ) : (
              <a href={href} rel="noopener noreferrer" target="_blank">
                {children}
              </a>
            ),
          p: ({ node, children }) => {
            const [only] = node.children as HastNode[];
            const text = node.children.length === 1 && only.type === 'text' ? only.value ?? '' : '';
            const videoId = youtubeId(text);

            return videoId ? <YoutubeEmbed videoId={videoId} /> : <p>{children}</p>;
          },
          li: ({ node, children }) =>
            findImage(node as HastNode) ? (
              <li>
                <MediaRow className="benefit" node={node as HastNode}>
                  {children}
                </MediaRow>
              </li>
            ) : (
              <li>{children}</li>
            ),
          blockquote: ({ node, children }) => (
            <MediaRow as="figure" className="quote" node={node as HastNode}>
              {children}
            </MediaRow>
          ),
        }}
      >
        {markdown}
      </ReactMarkdown>
    </Box>
  );
};

export default MarkdownPage;
